import { useEffect, useState } from 'react';
import {
  createSubject,
  getLatestBreedList,
  listSubjects,
  type Subject,
} from '../../lib/api/endpoints.js';
import { subjectBreed } from '../../lib/subjects.js';
import { Combobox } from '../../components/Combobox.js';
import { Select } from '../../components/Select.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';

/**
 * Species offered by default.
 *
 * Only the two the project scopes for v1, and only because a scientific name is
 * required and asking a pet owner to type "Canis familiaris" is a bad first
 * interaction. Adding a species here needs an ethogram to exist for it —
 * otherwise the contribute form loads an empty vocabulary and the person is
 * stuck with no explanation.
 */
const SPECIES = [
  { scientificName: 'Canis familiaris', commonName: 'Dog' },
  { scientificName: 'Felis catus', commonName: 'Cat' },
];

export function SubjectsPage() {
  const [subjects, setSubjects] = useState<Subject[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  // What to do on "Try again": reload the list for a load failure, nothing for a
  // failed add (the form still has what was typed — pressing Save again retries).
  const [retryLoad, setRetryLoad] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    scientificName: SPECIES[0]!.scientificName,
    breedOrMorph: '',
    sex: '' as '' | 'male' | 'female' | 'unknown',
    birthDate: '',
  });

  const [breeds, setBreeds] = useState<string[]>([]);

  const load = () => {
    setError(null);
    listSubjects()
      .then(setSubjects)
      .catch((e) => {
        setError(e);
        setRetryLoad(true);
      });
  };

  useEffect(load, []);

  // Breed suggestions for the species currently selected. Failure is silent by
  // design: the field is free text either way, and an error about a list of
  // hints would be noise in front of a form that works without it.
  useEffect(() => {
    let cancelled = false;
    setBreeds([]);

    getLatestBreedList(form.scientificName)
      .then((list) => {
        if (!cancelled) setBreeds(list.breeds);
      })
      .catch(() => {
        if (!cancelled) setBreeds([]);
      });

    return () => {
      cancelled = true;
    };
  }, [form.scientificName]);

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    setAdding(true);
    setError(null);

    try {
      const species = SPECIES.find((s) => s.scientificName === form.scientificName);
      await createSubject({
        name: form.name.trim() || null,
        scientificName: form.scientificName,
        commonName: species?.commonName ?? null,
        breedOrMorph: form.breedOrMorph.trim() || null,
        sex: form.sex || null,
        birthDate: form.birthDate || null,
        captiveStatus: 'domestic',
      });
      setForm({ ...form, name: '', breedOrMorph: '', birthDate: '' });
      load();
    } catch (e) {
      setError(e);
      setRetryLoad(false);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <p className="eyebrow">Your animals</p>
      <h1 className="page-title">Who you're recording</h1>
      <p className="page-intro">
        Observations belong to an individual, not a species. That is what gives each animal a
        timeline — and what lets a researcher treat ten recordings of your dog as ten measures of
        the same animal rather than ten unrelated samples.
      </p>

      {error !== null && (
        <ErrorNotice error={error} onRetry={retryLoad ? load : undefined} />
      )}

      {subjects && subjects.length > 0 && (
        <ul className="rows" style={{ marginBottom: '2.5rem' }}>
          {subjects.map((subject) => (
            <li key={subject.id}>
              <div>
                <strong>{subject.name ?? subject.commonName ?? subject.scientificName}</strong>
                <div className="meta" style={{ marginTop: '0.2rem' }}>
                  {[subjectBreed(subject), subject.commonName, subject.sex]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <a className="btn btn--quiet" href={`#/timeline/${subject.id}`}>
                Timeline
              </a>
            </li>
          ))}
        </ul>
      )}

      <form className="panel" onSubmit={add}>
        <p className="eyebrow" style={{ marginBottom: '1.25rem' }}>
          Add an animal
        </p>

        <div className="field">
          <label className="field-label" htmlFor="name">
            Name
          </label>
          <input
            id="name"
            type="text"
            placeholder="Bindi"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <p className="field-hint">Only ever shown to you. Never published with the corpus.</p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="species">
            Species
          </label>
          <Select
            id="species"
            value={form.scientificName}
            onChange={(value) => setForm({ ...form, scientificName: value })}
            options={SPECIES.map((s) => ({
              value: s.scientificName,
              label: s.commonName,
              description: s.scientificName,
            }))}
          />
          <p className="field-hint">
            Dogs and cats for now — those are the vocabularies that exist. More follow one species
            at a time, not all at once.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="breed">
            Breed or type
          </label>
          {/* Suggestions, never a restriction: anything typed here is kept as
              typed. The server decides separately whether what was typed also
              matches a published breed, which is the only thing the
              suggestions are for. */}
          <Combobox
            id="breed"
            value={form.breedOrMorph}
            onChange={(value) => setForm({ ...form, breedOrMorph: value })}
            options={breeds}
            placeholder="Border Collie"
          />
          <p className="field-hint">
            Start typing, or write your own — a mix, a rare breed and a guess are all fine answers.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="sex">
            Sex
          </label>
          <Select
            id="sex"
            value={form.sex}
            onChange={(value) => setForm({ ...form, sex: value as typeof form.sex })}
            options={[
              { value: '', label: 'Prefer not to say' },
              { value: 'female', label: 'Female' },
              { value: 'male', label: 'Male' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            maxWidth="14rem"
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="birth">
            Date of birth
          </label>
          <input
            id="birth"
            type="date"
            value={form.birthDate}
            onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
            style={{ maxWidth: '14rem' }}
          />
          <p className="field-hint">
            Stored rather than an age, so it stays correct instead of being correct on the day you
            entered it. Approximate is fine.
          </p>
        </div>

        <button type="submit" className="btn" disabled={adding}>
          {adding ? 'Adding…' : 'Add animal'}
        </button>
      </form>
    </div>
  );
}
